import { ApplicationRef, ChangeDetectionStrategy, ChangeDetectorRef, Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PerfEntry, perfPush, perfTraceEnabled } from './perf-trace';

interface PerfSummaryRow {
  fn: string;
  calls: number;
  totalMs: number;
}

interface Scorecard {
  frozenTotalMs: number;
  worstFreezeMs: number;
  slowestClickMs: number;
  serverTotalMs: number;
  serverCalls: number;
  slowestRequestMs: number;
  crunchTotalMs: number;
}

interface ScoreMetric {
  label: string;
  hint: string;
  now: number;
  before: number | null;
}

const BASELINE_KEY = 'perfHudBaseline';
const CLICK_EVENTS = ["input latency 'click'", "input latency 'pointerdown'", "input latency 'pointerup'", "input latency 'dblclick'", "input latency 'keydown'"];

@Component({
  selector: 'app-perf-hud',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (enabled) {
      <div class="perf-hud">
        <div class="perf-hud-head">
          <span>SPEED CHECK</span>
          <span class="perf-hud-actions">
            <a (click)="copy()">{{ copied ? 'copied ✓' : 'copy report' }}</a>
            <a (click)="clear()">reset</a>
          </span>
        </div>
        <div class="perf-hud-help">Reset, do ONE action (open a label, change a status…), then read:</div>

        <table class="perf-hud-score">
          <tr class="perf-hud-cols">
            <td></td>
            <td>now</td>
            @if (baseline) {
              <td>before</td>
              <td>change</td>
            }
          </tr>
          @for (m of metrics; track m.label) {
            <tr>
              <td class="perf-hud-metric" [title]="m.hint">{{ m.label }}</td>
              <td class="perf-hud-num">{{ fmt(m.now) }}</td>
              @if (baseline) {
                <td class="perf-hud-num perf-hud-dim">{{ m.before === null ? '—' : fmt(m.before) }}</td>
                <td class="perf-hud-num" [class.perf-good]="delta(m) < -5" [class.perf-bad]="delta(m) > 5">
                  {{ deltaText(m) }}
                </td>
              }
            </tr>
          }
        </table>

        <div class="perf-hud-foot">
          <a (click)="saveBaseline()">{{ baseline ? 'replace "before"' : 'save as "before"' }}</a>
          @if (baseline) {
            <span class="perf-hud-dim">before saved {{ baselineLabel }}</span>
          }
          <a class="perf-hud-right" (click)="showDetails = !showDetails; refreshNow()">{{ showDetails ? 'hide' : 'technical' }} details</a>
        </div>

        @if (showDetails) {
          <div class="perf-hud-details">
            @for (row of rows; track row.fn) {
              <div class="perf-hud-row">
                <span class="perf-hud-fn">{{ row.fn }}</span>
                <span class="perf-hud-num">{{ row.calls }}x</span>
                <span class="perf-hud-num">{{ row.totalMs | number: '1.0-0' }}ms</span>
              </div>
            }
          </div>
        }
      </div>
    }
  `,
  styles: [
    `
      .perf-hud {
        position: fixed;
        bottom: 8px;
        right: 8px;
        z-index: 99999;
        background: rgba(15, 18, 26, 0.94);
        color: #d7e1ee;
        font: 12px/1.6 'Segoe UI', sans-serif;
        border: 1px solid #3a4354;
        border-radius: 8px;
        padding: 8px 12px;
        min-width: 340px;
        max-width: 480px;
        max-height: 60vh;
        overflow-y: auto;
      }
      .perf-hud-head {
        display: flex;
        justify-content: space-between;
        color: #8ab4f8;
        font-weight: 600;
        letter-spacing: 0.5px;
      }
      .perf-hud-help {
        color: #8a94a6;
        font-size: 11px;
        margin: 2px 0 6px;
      }
      .perf-hud a {
        color: #8ab4f8;
        cursor: pointer;
        text-decoration: underline;
        margin-left: 10px;
        font-size: 11px;
      }
      .perf-hud-score {
        width: 100%;
        border-collapse: collapse;
      }
      .perf-hud-score td {
        padding: 2px 6px 2px 0;
      }
      .perf-hud-cols td {
        color: #8a94a6;
        font-size: 10px;
        text-transform: uppercase;
      }
      .perf-hud-metric {
        color: #d7e1ee;
      }
      .perf-hud-num {
        text-align: right;
        color: #ffd479;
        font-family: Consolas, monospace;
        white-space: nowrap;
      }
      .perf-hud-dim {
        color: #8a94a6;
      }
      .perf-good {
        color: #6fdc8c !important;
        font-weight: 600;
      }
      .perf-bad {
        color: #ff8389 !important;
        font-weight: 600;
      }
      .perf-hud-foot {
        display: flex;
        align-items: center;
        margin-top: 6px;
        gap: 8px;
      }
      .perf-hud-foot a:first-child {
        margin-left: 0;
      }
      .perf-hud-right {
        margin-left: auto !important;
      }
      .perf-hud-details {
        margin-top: 6px;
        border-top: 1px solid #3a4354;
        padding-top: 4px;
        font: 11px/1.5 Consolas, monospace;
      }
      .perf-hud-row {
        display: flex;
        gap: 8px;
      }
      .perf-hud-fn {
        flex: 1;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    `,
  ],
})
export class PerfHudComponent implements OnInit, OnDestroy {
  enabled = false;
  rows: PerfSummaryRow[] = [];
  metrics: ScoreMetric[] = [];
  baseline: Scorecard | null = null;
  baselineLabel = '';
  copied = false;
  showDetails = false;
  private timer: any = null;
  private consumed = -1;
  private observers: PerformanceObserver[] = [];

  constructor(
    private zone: NgZone,
    private cdr: ChangeDetectorRef,
    private appRef: ApplicationRef,
  ) {
    // allow enabling via URL so testers never need the console
    try {
      if (new URLSearchParams(window.location.search).get('perfTrace') === '1') {
        localStorage.setItem('perfTrace', '1');
      }
    } catch {
      /* no-op */
    }
    this.enabled = perfTraceEnabled();
  }

  ngOnInit(): void {
    if (!this.enabled) {
      return;
    }
    this.loadBaseline();
    this.zone.runOutsideAngular(() => {
      this.timer = setInterval(() => this.refresh(), 500);
      this.installObservers();
    });
    this.installTickProbe();
  }

  ngOnDestroy(): void {
    if (this.timer) {
      clearInterval(this.timer);
    }
    this.observers.forEach((o) => o.disconnect());
    this.observers = [];
  }

  fmt(ms: number): string {
    if (ms >= 10000) return (ms / 1000).toFixed(1) + ' s';
    if (ms >= 1000) return (ms / 1000).toFixed(2) + ' s';
    return Math.round(ms) + ' ms';
  }

  delta(m: ScoreMetric): number {
    if (m.before === null || m.before === 0) return 0;
    return ((m.now - m.before) / m.before) * 100;
  }

  deltaText(m: ScoreMetric): string {
    if (m.before === null) return '—';
    if (m.before === 0 && m.now === 0) return '=';
    if (m.before === 0) return 'new';
    const d = this.delta(m);
    if (Math.abs(d) <= 5) return '≈';
    return (d < 0 ? '▼ ' : '▲ ') + Math.abs(Math.round(d)) + '%';
  }

  saveBaseline(): void {
    const card = this.computeScorecard();
    const label = new Date().toLocaleString();
    localStorage.setItem(BASELINE_KEY, JSON.stringify({ label, card }));
    this.baseline = card;
    this.baselineLabel = label;
    this.refreshNow();
  }

  copy(): void {
    const lines = ['Speed check ' + new Date().toLocaleString(), ''];
    for (const m of this.metrics) {
      lines.push(`${m.label}: ${this.fmt(m.now)}` + (m.before !== null ? ` (before: ${this.fmt(m.before)}, ${this.deltaText(m)})` : ''));
    }
    lines.push('', '--- technical details ---');
    for (const r of this.rows) {
      lines.push(`${r.fn}: ${r.calls}x ${Math.round(r.totalMs)}ms`);
    }
    navigator.clipboard?.writeText(lines.join('\n'));
    this.copied = true;
    setTimeout(() => {
      this.copied = false;
      this.cdr.detectChanges();
    }, 1200);
  }

  clear(): void {
    (window as any).__perfTrace = [];
    this.consumed = -1;
    this.refreshNow();
  }

  refreshNow(): void {
    this.consumed = -1;
    this.refresh();
  }

  private loadBaseline(): void {
    try {
      const raw = localStorage.getItem(BASELINE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        this.baseline = parsed.card;
        this.baselineLabel = parsed.label;
      }
    } catch {
      this.baseline = null;
    }
  }

  private computeScorecard(): Scorecard {
    const entries: PerfEntry[] = (window as any).__perfTrace ?? [];
    const card: Scorecard = { frozenTotalMs: 0, worstFreezeMs: 0, slowestClickMs: 0, serverTotalMs: 0, serverCalls: 0, slowestRequestMs: 0, crunchTotalMs: 0 };
    for (const e of entries) {
      if (e.fn.startsWith('main-thread block')) {
        card.frozenTotalMs += e.ms;
        card.worstFreezeMs = Math.max(card.worstFreezeMs, e.ms);
      } else if (CLICK_EVENTS.includes(e.fn)) {
        card.slowestClickMs = Math.max(card.slowestClickMs, e.ms);
      } else if (/^(GET|POST|PUT|PATCH|DELETE) /.test(e.fn)) {
        card.serverTotalMs += e.ms;
        card.serverCalls++;
        card.slowestRequestMs = Math.max(card.slowestRequestMs, e.ms);
      } else if (e.fn.startsWith('VM rebuild') || e.fn === 'deepCopy' || e.fn.startsWith('change detection pass')) {
        card.crunchTotalMs += e.ms;
      }
    }
    return card;
  }

  private buildMetrics(card: Scorecard): ScoreMetric[] {
    const b = this.baseline;
    return [
      { label: 'UI frozen time (total)', hint: 'How long the app could not respond at all', now: card.frozenTotalMs, before: b?.frozenTotalMs ?? null },
      { label: 'Worst single freeze', hint: 'Longest uninterrupted freeze', now: card.worstFreezeMs, before: b?.worstFreezeMs ?? null },
      { label: 'Slowest click response', hint: 'Longest wait between a click/keypress and the screen reacting', now: card.slowestClickMs, before: b?.slowestClickMs ?? null },
      { label: 'Waiting for server (total)', hint: 'Time spent waiting for the backend, all requests', now: card.serverTotalMs, before: b?.serverTotalMs ?? null },
      { label: 'Slowest server request', hint: 'The single slowest backend call', now: card.slowestRequestMs, before: b?.slowestRequestMs ?? null },
    ];
  }

  private refresh(): void {
    const entries: PerfEntry[] = (window as any).__perfTrace ?? [];
    if (entries.length === this.consumed) {
      return;
    }
    this.consumed = entries.length;

    const byFn = new Map<string, PerfSummaryRow>();
    for (const e of entries) {
      let row = byFn.get(e.fn);
      if (!row) {
        row = { fn: e.fn, calls: 0, totalMs: 0 };
        byFn.set(e.fn, row);
      }
      row.calls++;
      row.totalMs += e.ms;
    }
    this.rows = Array.from(byFn.values()).sort((a, b) => b.totalMs - a.totalMs);
    this.metrics = this.buildMetrics(this.computeScorecard());
    this.cdr.detectChanges();
  }

  private installObservers(): void {
    try {
      const lt = new PerformanceObserver((list) => {
        list.getEntries().forEach((e) => perfPush('main-thread block (>=50ms)', e.duration));
      });
      lt.observe({ type: 'longtask' } as PerformanceObserverInit);
      this.observers.push(lt);
    } catch {
      /* longtask API unavailable */
    }
    try {
      // discrete interactions only — hover-family events flood the log with
      // overlapping durations that all describe the same blocked paint
      const tracked = new Set(['click', 'dblclick', 'contextmenu', 'pointerdown', 'pointerup', 'keydown', 'input', 'change', 'dragstart', 'drop']);
      const ev = new PerformanceObserver((list) => {
        list.getEntries().forEach((e) => {
          if (tracked.has(e.name)) {
            perfPush(`input latency '${e.name}'`, e.duration);
          }
        });
      });
      ev.observe({ type: 'event', durationThreshold: 50 } as PerformanceObserverInit);
      this.observers.push(ev);
    } catch {
      /* event timing API unavailable */
    }
  }

  // change detection: template execution + DOM update time per app-wide pass.
  // Angular 18+ schedules CD via the internal _tick, not the public tick().
  private installTickProbe(): void {
    const appRef = this.appRef as any;
    if (appRef.__perfTickPatched) {
      return;
    }
    appRef.__perfTickPatched = true;
    const tickKey = typeof appRef._tick === 'function' ? '_tick' : 'tick';
    const origTick = appRef[tickKey].bind(appRef);
    appRef[tickKey] = (...args: unknown[]) => {
      const t0 = performance.now();
      const result = origTick(...args);
      const ms = performance.now() - t0;
      if (ms >= 4) {
        perfPush('change detection pass (>=4ms)', ms);
      }
      return result;
    };
  }
}
